import React, { useState, useEffect, useContext } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import "./Header.css";
import { ConfigContext } from "../Context/ConfigContext";

function Header() {
    const { ApiUrl, apiHeaderJson, phone_no, social_media, email, createSlug } = useContext(ConfigContext);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);

    const navigate = useNavigate();

    // State for dynamic categories
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Scroll listener for sticky header glass effect
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 20) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close overlays with Escape key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                setIsSearchOpen(false);
                setIsDrawerOpen(false);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    // Lock body scroll when search or drawer is open
    useEffect(() => {
        if (isDrawerOpen || isSearchOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [isDrawerOpen, isSearchOpen]);

    const handleSearch = () => {
        setIsSearchOpen(false);
        setIsDrawerOpen(false);
        setSearchQuery('');
        navigate(`/products/0/${encodeURIComponent(searchQuery)}`)
    }

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${ApiUrl}apis/Categories`, {
                    headers: apiHeaderJson,
                });
                const { success, data } = response.data;
                if (success && Array.isArray(data)) {
                    // Map API response to component structure
                    const mapped = data.map((cat) => ({
                        id: cat.category_id,
                        name: cat.category_title,
                        // Map products to simple item objects (without badges)
                        items: (cat.products || []).map((prod) => ({
                            name: prod.product_title,
                            product_id: prod.product_id,
                            // Optional badge: you can add logic based on product properties if available
                            // For now, no badge
                        })),
                        // Assign an icon based on category name (fallback to default)
                        icon: getIconForCategory(cat.category_title),
                    }));
                    setCategories(mapped);
                } else {
                    setCategories([]);
                }
            } catch (err) {
                console.error("Failed to fetch categories:", err);
                setError(err.message);
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [ApiUrl, apiHeaderJson]);

    // Helper to assign icons (customize as needed)
    const getIconForCategory = (name) => {
        const lower = name.toLowerCase();
        if (lower.includes("biscuit") || lower.includes("cookie")) return "ri-cookie-line";
        if (lower.includes("cake") || lower.includes("treat")) return "ri-cake-3-line";
        if (lower.includes("bread") || lower.includes("bun")) return "ri-restaurant-line";
        if (lower.includes("dairy") || lower.includes("cheese") || lower.includes("milk")) return "ri-cup-line";
        if (lower.includes("snack")) return "ri-pizza-line";
        return "ri-grid-fill"; // default
    };

    return (
        <header className="header-wrapper">
            {/* ===== 2. MAIN NAVBAR ===== */}
            <nav className={`main-navbar ${isScrolled ? "scrolled" : ""}`}>
                <div className="container-fluid px-3 px-lg-4 d-flex align-items-center justify-content-between">
                    {/* Left: Mobile Hamburger Toggle */}
                    <div onClick={() => navigate('/')} >
                        <img
                            src="/icon-logo.png"
                            alt="Icon Logo"
                            className="header-icon-logo main-nav"
                            data-aos="fade-down"
                            data-aos-delay={500}
                        />
                    </div>

                    {/* Center: Brand Logo */}
                    <Link to="/" className="header-brand-logo" onClick={() => setIsDrawerOpen(false)}>
                        <div className="brand-text">
                            <img data-aos="fade-down" data-aos-delay={800} src="/royal-logo.png" alt="Royal Logo" />
                        </div>
                    </Link>

                    {/* Center: Desktop Navigation Menu */}
                    <ul className="nav-menu-desktop d-none">
                        <li>
                            <NavLink to="/" className="nav-item-link" end>
                                <i className="ri-home-5-line"></i> Home
                            </NavLink>
                        </li>

                        {/* Mega Menu Dropdown – dynamic from API */}
                        <li className="mega-dropdown-container">
                            <NavLink to="/products" className="nav-item-link">
                                <i className="ri-grid-fill"></i> Products{" "}
                                <i className="ri-arrow-down-s-line" style={{ fontSize: "0.9rem" }}></i>
                            </NavLink>
                            <div className="mega-menu">
                                {loading ? (
                                    <div style={{ padding: "1.5rem", textAlign: "center" }}>
                                        <span>Loading categories...</span>
                                    </div>
                                ) : error || categories.length === 0 ? (
                                    <div style={{ padding: "1.5rem", textAlign: "center" }}>
                                        <span>No categories available</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="row g-4">
                                            {categories.map((cat) => (
                                                <div className="col-md-3" key={cat.id}>
                                                    <div className="mega-category-title"
                                                        onClick={() => navigate(`/products/${createSlug(cat.name)}-${cat.id}`)} >

                                                        <i className={cat.icon}></i> {cat.name}
                                                    </div>
                                                    <ul className="mega-item-list">
                                                        {cat.items && cat.items.length > 0 ? (
                                                            cat.items.map((item, idx) => (
                                                                <li key={idx}>
                                                                    <Link
                                                                        to={`/products/${createSlug(cat.name)}-${cat.id}`}
                                                                        className="mega-item-link"
                                                                    >
                                                                        <span>{item.name}</span>
                                                                        {/* If you want to show badges, add them here */}
                                                                    </Link>
                                                                </li>
                                                            ))
                                                        ) : (
                                                            <li className="text-muted" style={{ fontSize: "0.85rem", padding: "4px 0" }}>
                                                                No products
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Mega Menu Bottom Banner */}
                                        <div className="mt-4 pt-3 border-top d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: "0.85rem" }}>
                                                <i className="ri-sparkles-fill text-warning"></i>
                                                <span>Crafted with 100% real butter & premium ingredients since 1892.</span>
                                            </div>
                                            <Link to="/products" className="text-decoration-none fw-bold" style={{ color: "#E31837", fontSize: "0.88rem" }}>
                                                View All Catalog <i className="ri-arrow-right-line"></i>
                                            </Link>
                                        </div>
                                    </>
                                )}
                            </div>
                        </li>

                        <li>
                            <Link to="/about" className="nav-item-link">
                                Our Story
                            </Link>
                        </li>
                        <li>
                            <Link to="/" className="nav-item-link">
                                Recipes
                            </Link>
                        </li>
                        <li>
                            <Link to="/" className="nav-item-link">
                                Sustainability
                            </Link>
                        </li>
                        <li>
                            <NavLink to="/contact" className="nav-item-link">
                                Contact
                            </NavLink>
                        </li>
                    </ul>

                    {/* Right: Header Toolbar Actions */}
                    <div className="header-actions">
                        <Link to="/inquiry" className="inquiry-btn" title="Send Inquiry">
                            <i className="ri-chat-3-line"></i>
                        </Link>
                        <button
                            className="hamburger-toggle"
                            type="button"
                            onClick={() => setIsDrawerOpen(true)}
                            aria-label="Open Navigation Drawer"
                        >
                            <i className="ri-menu-3-line"></i>
                        </button>
                    </div>
                </div>
            </nav>

            {/* ===== 3. MOBILE OFFCANVAS DRAWER ===== */}
            <div
                className={`mobile-drawer-backdrop ${isDrawerOpen ? "open" : ""}`}
                onClick={() => setIsDrawerOpen(false)}
            />
            <aside className={`mobile-drawer ${isDrawerOpen ? "open" : ""}`}>
                <div className="drawer-header">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <img src="/icon-logo.png" alt="Icon Logo" className="header-icon-logo" />
                        <img src="/royal-logo.png" alt="Royal Logo" width="150px" />
                        <button
                            className="drawer-close-btn"
                            onClick={() => setIsDrawerOpen(false)}
                            aria-label="Close Menu"
                        >
                            <i className="ri-close-line"></i>
                        </button>
                    </div>
                </div>

                <div className="drawer-body">
                    {/* Mobile Quick Search */}
                    <div className="drawer-search">
                        <i className="ri-search-line"></i>
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => {
                                setIsDrawerOpen(false);
                                setIsSearchOpen(true);
                            }}
                        />
                    </div>

                    {/* Drawer Menu */}
                    <ul className="drawer-menu">
                        <li className="drawer-menu-item">
                            <Link to="/" className="drawer-menu-link" onClick={() => setIsDrawerOpen(false)}>
                                <span><i className="ri-home-5-line me-2"></i> Home</span>
                                <i className="ri-arrow-right-s-line text-muted"></i>
                            </Link>
                        </li>

                        {/* Collapsible Mobile Products Accordion – dynamic */}
                        <li className="drawer-menu-item">
                            <button
                                className="drawer-accordion-btn"
                                onClick={() => setIsMobileProductsOpen(!isMobileProductsOpen)}
                            >
                                <span><i className="ri-grid-fill me-2" style={{ color: "#E31837" }}></i> Products</span>
                                <i className={isMobileProductsOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
                            </button>
                            {isMobileProductsOpen && (
                                <div className="drawer-accordion-content">
                                    {loading ? (
                                        <div style={{ padding: "0.5rem", textAlign: "center" }}>Loading...</div>
                                    ) : error || categories.length === 0 ? (
                                        <div style={{ padding: "0.5rem", color: "#888" }}>No categories</div>
                                    ) : (
                                        categories.map((cat) => (
                                            <div className="drawer-sub-category" key={cat.id}>
                                                {cat.items && cat.items.length > 0 &&
                                                    <div className="drawer-sub-title"
                                                        onClick={() => {
                                                            navigate(`/products/${createSlug(cat.name)}-${cat.id}`)
                                                            setIsDrawerOpen(false)
                                                        }}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <i className={`${cat.icon} me-1`}></i> {cat.name}
                                                    </div>}
                                                {cat.items && cat.items.length > 0 && (
                                                    cat.items.map((item, idx) => (
                                                        <Link
                                                            to={`/product/${createSlug(item.name)}-${item.product_id}`}
                                                            className="drawer-sub-link"
                                                            onClick={() => setIsDrawerOpen(false)}
                                                        >
                                                            {item.name}
                                                        </Link>
                                                    ))
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </li>

                        <li className="drawer-menu-item">
                            <Link to="/about" className="drawer-menu-link" onClick={() => setIsDrawerOpen(false)}>
                                <span><i className="ri-history-line me-2"></i> About Us</span>
                                <i className="ri-arrow-right-s-line text-muted"></i>
                            </Link>
                        </li>

                        <li className="drawer-menu-item">
                            <NavLink to="/contact" className="drawer-menu-link" onClick={() => setIsDrawerOpen(false)}>
                                <span><i className="ri-phone-line me-2"></i> Contact Us</span>
                                <i className="ri-arrow-right-s-line text-muted"></i>
                            </NavLink>
                        </li>

                        <li className="drawer-menu-item">
                            <NavLink to="/inquiry" className="drawer-menu-link" onClick={() => setIsDrawerOpen(false)}>
                                <span><i className="ri-chat-3-line me-2"></i> Inquiry</span>
                                <i className="ri-arrow-right-s-line text-muted"></i>
                            </NavLink>
                        </li>
                    </ul>
                </div>

                <div className="drawer-footer">
                    <div className="drawer-contact-info">
                        <div>
                            <a href={`tel:${phone_no}`} target="_blank" className="text-danger text-decoration-none" rel="noopener noreferrer">
                                <i className="ri-customer-service-2-line text-danger"></i> Phone No: <span className="text-dark">{phone_no}</span>
                            </a>
                        </div>
                        <div>
                            <a href={`mailto:${email}`} target="_blank" className="text-decoration-none text-danger" rel="noopener noreferrer">
                                <i className="ri-mail-line text-danger"></i> Email: <span className="text-dark"> {email} </span>
                            </a>
                        </div>
                    </div>

                    <div className="drawer-social">
                        <Link target="_blank" to={social_media.facebook} aria-label="Facebook"><i className="ri-facebook-fill"></i></Link>
                        <Link target="_blank" to={social_media.instagram} aria-label="Instagram"><i className="ri-instagram-line"></i></Link>
                        <Link target="_blank" to={social_media.twitter} aria-label="Twitter"><i className="ri-twitter-x-line"></i></Link>
                    </div>
                </div>
            </aside>

            {/* ===== 4. FULL-SCREEN SEARCH OVERLAY MODAL ===== */}
            {isSearchOpen && (
                <div className="search-modal-overlay">
                    <div className="search-modal-content">
                        <button
                            className="search-close-btn"
                            onClick={() => setIsSearchOpen(false)}
                            aria-label="Close Search"
                        >
                            <i className="ri-close-line"></i>
                        </button>

                        <div className="search-input-box">
                            <i className="ri-search-line"></i>
                            <input
                                type="text"
                                placeholder="Search for biscuits, cakes, breads..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                autoFocus
                            />
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}

export default Header;